import { encryptFileForDownload, isEncryptionEnabled } from '@/api/encryptionApi';
import { appendFileDownloadQuery, normalizeFileAccessUrlForBrowser } from '@/utils/fileAccessUrl';

function toAbsoluteFetchUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return new URL(normalized, window.location.origin).href;
}

export type DownloadBytes = { loaded: number; total?: number };

export async function responseBlobWithProgress(
  res: Response,
  onProgress?: (progress: DownloadBytes) => void,
): Promise<Blob> {
  const length = Number(res.headers.get('content-length'));
  const total = Number.isFinite(length) && length > 0 ? length : undefined;
  if (!res.body || !onProgress) {
    const blob = await res.blob();
    onProgress?.({ loaded: blob.size, total });
    return blob;
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress({ loaded, total });
  }
  return new Blob(chunks as BlobPart[], { type: res.headers.get('content-type') || '' });
}

async function fetchAccessUrlBlob(path: string, onProgress?: (progress: DownloadBytes) => void): Promise<Blob | null> {
  const res = await fetch(toAbsoluteFetchUrl(path));
  if (!res.ok) return null;
  return responseBlobWithProgress(res, onProgress);
}

function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** 触发浏览器下载；若加解密已启用则先经后端加密再落盘 */
export async function downloadEncryptedBlob(blob: Blob, fileName: string): Promise<void> {
  const payload = (await isEncryptionEnabled())
    ? await encryptFileForDownload(blob, fileName)
    : blob;
  triggerBrowserDownload(payload, fileName);
}

/**
 * 从脱敏 accessUrl 下载文件。
 * 优先 download=1 走服务端加密通道；失败时回退为拉取预览同源内容再经 encrypt-for-download 交付。
 */
export async function downloadAccessUrlFile(
  accessUrl: string,
  fileName: string,
  onProgress?: (progress: DownloadBytes) => void,
  onProcessing?: () => void,
): Promise<void> {
  const plainPath = normalizeFileAccessUrlForBrowser(accessUrl);
  const downloadPath = normalizeFileAccessUrlForBrowser(appendFileDownloadQuery(accessUrl));

  const encryptedBlob = await fetchAccessUrlBlob(downloadPath, onProgress);
  if (encryptedBlob) {
    triggerBrowserDownload(encryptedBlob, fileName);
    return;
  }

  onProgress?.({ loaded: 0, total: undefined });
  const plainBlob = await fetchAccessUrlBlob(plainPath, onProgress);
  if (!plainBlob) {
    throw new Error('HTTP 404');
  }
  onProcessing?.();
  const payload = await encryptFileForDownload(plainBlob, fileName);
  triggerBrowserDownload(payload, fileName);
}
